import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Flex, Grid, Text, VStack } from '@chakra-ui/react'
import { LuArrowLeft as ArrowLeft, LuFolderOpen } from 'react-icons/lu'
import { toaster } from '@minetrace/ui'
import { shipmentsService } from '../../../api/services/shipments.service'
import type { ShipmentDetailResponse } from '../../../api/models/response'
import { brand, themeColors } from '../../../theme'
import {
  enrichPoints,
  detectStops,
  computeTripStats,
  computeSegmentStats,
  buildSpeedProfile,
  buildColoredSegments,
  matchPhotosToPoints,
} from '../../../lib/trackAnalytics'
import TripInfoCard from './components/TripInfoCard'
import TripKpiGrid from './components/TripKpiGrid'
import RouteMap from './components/RouteMap'
import MapOverlayStats from './components/MapOverlayStats'
import SpeedProfileChart from './components/SpeedProfileChart'
import StopsList from './components/StopsList'
import ShipmentEventsTimeline from './components/ShipmentEventsTimeline'
import PhotoGallery from './components/PhotoGallery'
import PhotoLightbox from './components/PhotoLightbox'
import GpsPointsTable from './components/GpsPointsTable'
import CarbonFootprintPanel from './components/CarbonFootprintPanel'

const colors = themeColors

// El expediente del lote nace al cerrar la recepción (o al cancelarse el traslado): en
// tránsito o en garita todavía no existe.
const ESTADOS_SIN_EXPEDIENTE = new Set([0, 1, 2, 3, 4, 8]) // 8 = Finalizado: llegó a planta, sin recepción aún

interface Selection {
  a: number | null
  b: number | null
}

export default function DetalleRecorridoPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [shipment, setShipment] = useState<ShipmentDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const [showHeatmap, setShowHeatmap] = useState(false)
  const [colorBySpeed, setColorBySpeed] = useState(false)
  const [showSnapped, setShowSnapped] = useState(true)
  const [snapping, setSnapping] = useState(false)
  const [selection, setSelection] = useState<Selection>({ a: null, b: null })
  const [focus, setFocus] = useState<{ lat: number; lng: number; nonce: number } | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const focusNonce = useRef(0)

  useEffect(() => {
    const fetchShipment = async () => {
      if (!id) return
      setLoading(true)
      setError(false)
      try {
        setShipment(await shipmentsService.getById(id))
      } catch {
        setError(true)
      } finally {
        setLoading(false)
      }
    }
    fetchShipment()
  }, [id])

  // ── Analítica derivada (hooks antes de cualquier return) ──
  const enriched = useMemo(() => (shipment ? enrichPoints(shipment.locationPoints) : []), [shipment])
  const stops = useMemo(() => detectStops(enriched), [enriched])
  const tripStats = useMemo(() => computeTripStats(enriched, stops), [enriched, stops])
  const profile = useMemo(() => buildSpeedProfile(enriched), [enriched])
  const coloredSegments = useMemo(() => buildColoredSegments(enriched), [enriched])
  const photosWithTime = useMemo(
    () => (shipment ? matchPhotosToPoints(shipment.photos, enriched) : []),
    [shipment, enriched],
  )
  const segment = useMemo(
    () =>
      selection.a != null && selection.b != null
        ? computeSegmentStats(enriched, stops, selection.a, selection.b)
        : null,
    [enriched, stops, selection],
  )

  const onPointClick = useCallback((index: number) => {
    setSelection((prev) => {
      if (prev.a == null) return { a: index, b: null }
      if (prev.b == null) return index === prev.a ? prev : { a: prev.a, b: index }
      return { a: index, b: null }
    })
  }, [])

  const clearSelection = useCallback(() => setSelection({ a: null, b: null }), [])

  const focusOn = useCallback((lat: number, lng: number) => {
    focusNonce.current += 1
    setFocus({ lat, lng, nonce: focusNonce.current })
  }, [])

  // "Afinar ruta": snapea on-demand la traza de un traslado ya terminado sin ruta
  // ajustada, y actualiza el mapa en el acto con la polilínea que devuelve el backend.
  const handleSnapRoute = useCallback(async () => {
    if (!shipment) return
    setSnapping(true)
    try {
      const res = await shipmentsService.snapRoute(shipment.id)
      if (res.snappedRoutePolyline) {
        setShipment((prev) => (prev ? { ...prev, snappedRoutePolyline: res.snappedRoutePolyline } : prev))
        setShowSnapped(true)
        toaster.success({ description: res.snapped ? 'Ruta ajustada a la red vial' : 'La ruta ya estaba ajustada' })
      } else {
        toaster.error({ description: 'No se pudo afinar la ruta (sin tramos en vía o Roads API no disponible)' })
      }
    } catch {
      toaster.error({ description: 'No se pudo afinar la ruta' })
    } finally {
      setSnapping(false)
    }
  }, [shipment])

  if (loading) {
    return (
      <VStack gap={4} py={20}>
        <Text color={colors.textMuted} fontSize="sm">
          Cargando detalle del traslado...
        </Text>
      </VStack>
    )
  }

  if (error || !shipment) {
    return (
      <VStack gap={4} py={20}>
        <Text color={colors.text} fontSize="lg">
          Traslado no encontrado
        </Text>
        <Box
          as="button"
          color={brand.green}
          fontSize="sm"
          cursor="pointer"
          _hover={{ textDecoration: 'underline' }}
          onClick={() => navigate('/app/traslados')}
        >
          Volver a Traslados
        </Box>
      </VStack>
    )
  }

  const hasPoints = enriched.length > 0
  const hasPhotos = photosWithTime.length > 0
  const hasEvents = (shipment.stateChanges?.length ?? 0) > 0 || (shipment.pauseEvents?.length ?? 0) > 0
  const awaitingSecond = selection.a != null && selection.b == null

  return (
    <>
      <VStack gap={6} alignItems="stretch">
        {/* Breadcrumb + Back */}
        <Flex alignItems="center" gap={3}>
          <Box
            as="button"
            p={2}
            borderRadius="xl"
            cursor="pointer"
            color={colors.textMuted}
            _hover={{ bg: colors.bgHover, color: colors.text }}
            transition="all 0.2s"
            onClick={() => navigate('/app/traslados')}
          >
            <ArrowLeft size={20} />
          </Box>
          <Flex alignItems="center" gap={2}>
            <Text
              fontSize="sm"
              color={colors.textMuted}
              cursor="pointer"
              _hover={{ color: brand.green }}
              onClick={() => navigate('/app/traslados')}
            >
              Traslados
            </Text>
            <Text fontSize="sm" color={colors.textDimmed}>
              /
            </Text>
            <Text fontSize="sm" color={colors.text} fontWeight="600">
              Recorrido {shipment.batchCode}
            </Text>
          </Flex>
          {!ESTADOS_SIN_EXPEDIENTE.has(shipment.shipmentState) && (
            <Box
              as="button"
              ml="auto"
              display="flex"
              alignItems="center"
              gap={1.5}
              px={3}
              py={1.5}
              bg={colors.bgHover}
              color={colors.text}
              border="1px solid"
              borderColor={colors.border}
              borderRadius="lg"
              fontSize="xs"
              fontWeight="600"
              cursor="pointer"
              _hover={{ bg: colors.bg }}
              onClick={() =>
                navigate(`/app/identidad/expedientes/${encodeURIComponent(shipment.batchCode)}/electronico`)
              }
            >
              <LuFolderOpen size={14} />
              Expediente electrónico del lote
            </Box>
          )}
        </Flex>

        <TripInfoCard shipment={shipment} />

        <CarbonFootprintPanel shipmentId={shipment.id} shipmentState={shipment.shipmentState} />

        {hasPoints && <TripKpiGrid stats={tripStats} />}

        {hasPoints && (
          <RouteMap
            shipmentId={shipment.id}
            enriched={enriched}
            coloredSegments={coloredSegments}
            stops={stops}
            photos={photosWithTime}
            stateChanges={shipment.stateChanges ?? []}
            pauseEvents={shipment.pauseEvents ?? []}
            snappedRoutePolyline={shipment.snappedRoutePolyline}
            showSnapped={showSnapped}
            snapping={snapping}
            onSnapRoute={handleSnapRoute}
            showHeatmap={showHeatmap}
            colorBySpeed={colorBySpeed}
            onToggleSnapped={() => setShowSnapped((v) => !v)}
            onToggleHeatmap={() => setShowHeatmap((v) => !v)}
            onToggleColorBySpeed={() => setColorBySpeed((v) => !v)}
            selection={selection}
            segment={segment}
            onPointClick={onPointClick}
            focus={focus}
            onPhotoClick={(idx) => setLightboxIndex(idx)}
          >
            <MapOverlayStats segment={segment} awaitingSecond={awaitingSecond} onClear={clearSelection} />
          </RouteMap>
        )}

        {hasPoints && <SpeedProfileChart profile={profile} stops={stops} delay={0.15} />}

        {(hasEvents || hasPoints) && (
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
            <ShipmentEventsTimeline
              pauseEvents={shipment.pauseEvents ?? []}
              stateChanges={shipment.stateChanges ?? []}
              onFocus={focusOn}
              delay={0.18}
            />
            {hasPoints && (
              <StopsList
                stops={stops}
                onFocusStop={(stop) => focusOn(stop.lat, stop.lng)}
                delay={0.2}
              />
            )}
          </Grid>
        )}

        {hasPhotos && (
          <PhotoGallery
            shipmentId={shipment.id}
            photos={photosWithTime}
            onOpen={(idx) => setLightboxIndex(idx)}
            delay={0.25}
          />
        )}

        {hasPoints && <GpsPointsTable points={enriched} selection={selection} delay={0.3} />}
      </VStack>

      <PhotoLightbox
        shipmentId={shipment.id}
        photos={photosWithTime}
        index={lightboxIndex}
        onClose={() => setLightboxIndex(null)}
        onNavigate={(idx) => setLightboxIndex(idx)}
      />
    </>
  )
}
